import type { AdvLine, AdvScene } from './adv'

// 会話場面の「声」の台詞を、 人物像 (台帳 docs/ledger/story.md「いまの仕様（声）」・10-31 作者の9問の答え) に合わせた版。
//   配布版の既定 (10-31 作者の答え「直してから入れる」・プロローグの3行は作者の文)。 前の台詞 (src/adv_scripts.ts の本文) は開発版の URL advVoice=old で出る。
//   人懐っこい・ちょっとおしゃべりな軽口で、 褒めも慰めもする。 語彙は LUNAIRE と同じ人。 わざわざ短くしない。
//   決まり文句を繰り返さない。 後半ほど口数を減らし、 先代の部屋では話さない (配布版のまま「声は、まだ、聞こえなかった。」)。
//   「——」は間が空く所だけ。 もとの自我が出る行 (深刻な時・常夜燈の本当の事に近づく時) に寄せて置いた。
//   どこで・なぜ自我が出るか＝docs/research/2026-10-31-adv-voice-v2.md。
// 場面ごとに、 配布版の声の行を出てくる順に置き換える (null＝その行を出さない)。 数が合わない時はテストが落ちる。
export const ADV_VOICE_V2: Record<string, (string | null)[]> = {
  // プロローグ (稽古の前)
  'prebattle:nameless-shade': [
    'いるよ。みんな、いる。',
    'んー、道案内かな。あの城、見覚え、ない？',
    '知ってるはずだよ。だって、あなたの城だもの。',
    'そうだよ。あなたは、この城の姫なの。',
    'ほら、見て。',
    'みんな、出られないんだよね。王さまの力で。',
    '王さまって、自分でそう呼んでるだけ。外から来て、城ごと取っちゃったの。ひどいよね。',
    '斬ってあげれば、救ってあげられる。',
    'うん、いいよ。無理にとは言わない。けど、あなたにしかできないことだからなぁ。',
    '呼んでも、意味ないよ。みんな、出られないからね。',
    'そうだよ。月喰いの王が来てから、ずっと出られてないの。',
    '斬ってあげるしか救う方法はないの。',
    'じゃあ、行こうか。',
    'あれが、月喰いの王。',
    '席だって。モテるね、あなた。',
    'その前に、ちょっと練習しようか。戦い方は、私が説明してあげる。',
  ],
  'interlude:nameless-shade': [
    'ね、できたでしょ。最初にしては、上手だったよ。',
  ],
  'prebattle:dream-hound': [
    '空いてるね。座っちゃう？',
    '待ちくたびれちゃったんだね。じゃあ、あなたが楽にしてあげて。',
  ],
  'interlude:dream-hound': [
    'うん。ちゃんと、寝かせてあげられたね。',
  ],
  'prebattle:vigil-knight': [
    '頑固ねえ。',
    'ずっと立ちっぱなしなんだもの。そろそろ、休ませてあげよう？',
  ],
  'interlude:vigil-knight': [
    'よく頑張ったね。これで、あの騎士も、もう構えなくていいよ。',
  ],
  'prebattle:bride': [
    'わ、ごちそう。……の、跡だね。',
    'あなた、花婿になっちゃったね。',
  ],
  'prebattle:the-executioner': [
    '——……ご丁寧ね。あなたの分まで、空けてあるなんて。',
  ],
  // もとの自我：何度もやってきた手つきに LUNAIRE が気づいた直後。 先代も「最後まで」やった人。
  'interlude:the-executioner': [
    '——最後まで、か。……うん、行こう。',
  ],
  'prebattle:crowned-king-awoken': [
    'あらら。今度は、座る所もないね。',
    '違う違う。別の獣だよ。',
    'ほらね。もう、言葉も無いでしょ。',
    'はい、そこまで。考えるのは、あとでいいの。',
  ],
  // もとの自我：青い灯が見えた直後。 さえぎったまま、 言い切れずに揺らぐ。
  'interlude:crowned-king-awoken': [
    '——……うん。楽に、なれたよ。',
  ],
  'prebattle:inverted-jester': [
    '気にしないで。あの子、いつもああなの。',
    null,
  ],
  // もとの自我：LUNAIRE が「終わらせた数を数えている」と気づいた直後。
  'interlude:inverted-jester': [
    '——……数えなくて、いいのにね。',
  ],
  'prebattle:blade-dancer': [
    '拍手は、いらないんだって。じゃあ、最後まで見ててあげればいいよ。',
  ],
  // 光の流れる先を目で追った LUNAIRE を、 上がったままの手へ向け直す (知っていて言わない)。
  'interlude:blade-dancer': [
    'ほら、そっちじゃないよ。手、上がったままだよ。',
  ],
  'prebattle:hollow-actor': [
    'ずいぶん、待たせてるのね。',
    'あら。あなたまで、駒にされちゃった。',
    null,
  ],
  // もとの自我：「あなたは、迷わないの？」に LUNAIRE が「迷ったよ」と答えた直後。
  'interlude:hollow-actor': [
    '——うん。迷ったまま、ここまで来ちゃったね。',
  ],
}

// 「残る」のエンディングの終わり (10-31 作者 q4)：LUNAIRE が笑う絵 (まだ無い) の所で、 声の台詞のような最初の一言を言い、 正体が分かる。
//   置き場所＝常夜燈が光を呑む暗転のあと。 最初の一言はプロローグで声が LUNAIRE に最初に名乗った一言と同じにする。
export const ADV_STAY_V2_TAIL: AdvLine[] = [
  { kind: 'beat', ms: 1200 },
  { kind: 'lunaire', speech: 'spoken', text: 'んー、道案内かな。あの城、見覚え、ない？', pre: 600 },
  { kind: 'beat', ms: 900 },
  { kind: 'lunaire', speech: 'spoken', text: 'うん、こんな感じでいいか。これなら「私」も迷わないよね。', pace: 'slow' },
  { kind: 'beat', ms: 2000 },
]

let advVoiceV2 = true
export function setAdvVoiceV2(on: boolean): void { advVoiceV2 = on }

export function applyAdvVoiceV2(scene: AdvScene): AdvScene {
  if (!advVoiceV2) return scene
  if (scene.id === 'ending:stay') return { ...scene, lines: [...scene.lines, ...ADV_STAY_V2_TAIL] }
  const v2 = ADV_VOICE_V2[scene.id]
  if (!v2) return scene
  let i = 0
  const lines: AdvLine[] = []
  for (const line of scene.lines) {
    if (line.kind !== 'voice') { lines.push(line); continue }
    const text = v2[i++]
    if (text === undefined) lines.push(line)
    else if (text !== null) lines.push({ ...line, text })
  }
  return { ...scene, lines }
}
